import React from 'react';
import {
  Box,
  Flex,
  Checkbox,
  Button,
  useToast,
  Spinner,
  Select,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Divider,
} from '@chakra-ui/react';
import xhr from 'xhr';
import dayjs from 'dayjs';

function getPayloadList(res) {
  const payload = res?.data ?? res;
  return Array.isArray(payload) ? payload : payload?.data || [];
}

function getProgramValue(program) {
  return `${program?.program_type?.id}-${program?.id}`;
}

function getProgramTypeKey(program) {
  return program?.program_type?.type?.replace('-', '_');
}

function getSubjectOrCourseId(enrollment) {
  return enrollment?.subject ? enrollment.subject.id : enrollment?.course?.id;
}

function getPaymentSubjectIds(payment) {
  const subjects = payment?.subjects || [];

  if (!Array.isArray(subjects)) return [];

  return subjects
    .map((subject) => {
      if (typeof subject === 'number') return subject;
      if (typeof subject === 'string') return Number(subject);
      return subject?.id;
    })
    .filter(Boolean);
}

export default function EnrollSubjectsFromPaymentModal({
  isOpen,
  onClose,
  student,
  payment,
  payments = [],
  setPayments,
}) {
  const toast = useToast();

  const [programs, setPrograms] = React.useState([]);
  const [loadingPrograms, setLoadingPrograms] = React.useState(false);
  const [loadingSubjects, setLoadingSubjects] = React.useState(false);
  const [loadingEnrollments, setLoadingEnrollments] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [programValue, setProgramValue] = React.useState('');
  const [currentProgram, setCurrentProgram] = React.useState(null);

  const [allSubjects, setAllSubjects] = React.useState([]);
  const [subjectsByPeriod, setSubjectsByPeriod] = React.useState({});
  const [currentPeriod, setCurrentPeriod] = React.useState('');
  const [isPeriodMenu, setIsPeriodMenu] = React.useState(false);

  const [subjectEnrollments, setSubjectEnrollments] = React.useState([]);
  const [selectedSubjects, setSelectedSubjects] = React.useState([]);

  const studentId = student?.id;
  const selectedSubjectIds = selectedSubjects.map((item) => item.id);

  const displayedSubjects = React.useMemo(() => {
    if (!isPeriodMenu) return allSubjects;

    if (!currentPeriod) return [];

    return subjectsByPeriod[currentPeriod] || [];
  }, [allSubjects, isPeriodMenu, subjectsByPeriod, currentPeriod]);

  const enrolledIds = React.useMemo(() => {
    return subjectEnrollments
      .filter((enrollment) => enrollment?.is_trial !== true)
      .map(getSubjectOrCourseId)
      .filter(Boolean);
  }, [subjectEnrollments]);

  const resetState = () => {
    setProgramValue('');
    setCurrentProgram(null);
    setAllSubjects([]);
    setSubjectsByPeriod({});
    setCurrentPeriod('');
    setIsPeriodMenu(false);
    setSubjectEnrollments([]);
    setSelectedSubjects([]);
  };

  const handleClose = () => {
    resetState();

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  React.useEffect(() => {
    const loadPrograms = async () => {
      if (!isOpen || !studentId) return;

      try {
        setLoadingPrograms(true);

        const response = await xhr(`/students/${studentId}/programs`);

        setPrograms(response?.data || []);
      } catch (error) {
        console.error('Error cargando programas:', error);

        toast({
          title: 'Error',
          description: 'No se pudieron cargar los programas del estudiante.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoadingPrograms(false);
      }
    };

    loadPrograms();
  }, [isOpen, studentId, toast]);

  const handleProgramChange = async (value) => {
    setProgramValue(value);
    setCurrentProgram(null);
    setAllSubjects([]);
    setSubjectsByPeriod({});
    setCurrentPeriod('');
    setIsPeriodMenu(false);
    setSubjectEnrollments([]);
    setSelectedSubjects([]);

    if (!value) return;

    const program = programs.find((item) => getProgramValue(item) === value);

    if (!program) return;

    setCurrentProgram(program);

    const isAnnualProgram =
      program?.program_type?.slug === 'bachillerato-anual-para-adolescentes' ||
      program?.program_type?.slug === 'primaria-anual-para-ninos' ||
      program?.program_type?.slug === 'bachillerato-flexible';

    const shouldUsePeriodMenu =
      program?.program_type?.type === 'technical_career' || isAnnualProgram;

    setIsPeriodMenu(shouldUsePeriodMenu);

    try {
      setLoadingSubjects(true);

      if (program.is_category) {
        const response = await xhr(
          `/course-enrollments?filters[course][id][$eq]=${program.id}&filters[student][id][$eq]=${studentId}&populate=course.courses`
        );

        const list = response?.data?.data?.[0]?.course?.courses || [];

        setAllSubjects(list);
        setSubjectsByPeriod({});
      } else {
        const programTypeKey = getProgramTypeKey(program);

        const response = await xhr(
          `/subjects?filters[${programTypeKey}][id][$eq]=${program.id}&filters[visible][$eq]=true&sort[0]=order:asc&sort[1]=id:asc&populate=*&pagination[limit]=-1`
        );

        const subjects = getPayloadList(response);

        const grouped = subjects.reduce((acc, subject) => {
          const period = subject?.period || 'sin-periodo';

          if (!acc[period]) acc[period] = [];

          acc[period].push(subject);

          return acc;
        }, {});

        setAllSubjects(subjects);
        setSubjectsByPeriod(grouped);

        if (shouldUsePeriodMenu) {
          const firstPeriod = Object.keys(grouped).sort()[0] || '';

          setCurrentPeriod(firstPeriod);
        }
      }
    } catch (error) {
      console.error('Error cargando materias:', error);

      toast({
        title: 'Error',
        description: 'No se pudieron cargar las materias del programa.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingSubjects(false);
    }
  };

  React.useEffect(() => {
    const loadSubjectEnrollments = async () => {
      if (!currentProgram || !studentId) return;

      try {
        setLoadingEnrollments(true);

        if (currentProgram.is_category) {
          const response = await xhr(
            `/course-enrollments?filters[student][id][$eq]=${studentId}&filters[course_academy][id][$eq]=${currentProgram.id}&populate=course`
          );

          setSubjectEnrollments(getPayloadList(response));
        } else {
          const programTypeKey = getProgramTypeKey(currentProgram);

          const response = await xhr(
            `/subject-enrollments?filters[student][id][$eq]=${studentId}&filters[subject][id][$gte]=1&filters[subject][${programTypeKey}][id][$eq]=${currentProgram.id}&pagination[limit]=-1&populate=*`
          );

          setSubjectEnrollments(getPayloadList(response));
        }
      } catch (error) {
        console.error('Error cargando matrículas existentes:', error);

        setSubjectEnrollments([]);
      } finally {
        setLoadingEnrollments(false);
      }
    };

    loadSubjectEnrollments();
  }, [currentProgram, studentId]);

  const handleCheck = (subject, checked) => {
    if (checked) {
      setSelectedSubjects((prev) => [...prev, subject]);
    } else {
      setSelectedSubjects((prev) => prev.filter((item) => item.id !== subject.id));
    }
  };

  const saveEnrollments = async () => {
    if (!studentId || !payment?.id || !currentProgram) {
      toast({
        title: 'Faltan datos',
        description: 'No se encontró el estudiante, el pago o el programa seleccionado.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });

      return;
    }

    if (!selectedSubjects.length) {
      toast({
        title: 'Selecciona materias',
        description: 'Debes seleccionar al menos una materia para matricular.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });

      return;
    }

    try {
      setSaving(true);

      for (const subject of selectedSubjects) {
        const existingEnrollment = subjectEnrollments.find(
          (enrollment) => Number(getSubjectOrCourseId(enrollment)) === Number(subject.id)
        );

        if (existingEnrollment?.id && existingEnrollment?.is_trial === true) {
          await xhr.put(`/subject-enrollments/${existingEnrollment.id}`, {
            data: {
              is_trial: false,
            },
          });

          continue;
        }

        if (existingEnrollment?.id) {
          continue;
        }

        if (currentProgram.is_category) {
          const courseEnrollmentResponse = await xhr.post('/course-enrollments', {
            data: {
              status: 'estudiando',
              progress: 0,
              active: true,
              student: studentId,
              course: subject.id,
              course_academy: currentProgram.id,
            },
          });

          const courseEnrollmentId = courseEnrollmentResponse?.data?.data?.id;

          if (courseEnrollmentId) {
            const enrollmentResponse = await xhr(
              `/course-enrollments/${courseEnrollmentId}?populate=course.subject`
            );

            const subjectId = enrollmentResponse?.data?.data?.course?.subject?.id;

            if (subjectId) {
              await xhr.post('/subject-enrollments', {
                data: {
                  student: studentId,
                  subject: subjectId,
                  course: subject.id,
                },
              });
            }
          }
        } else {
          const programTypeKey = getProgramTypeKey(currentProgram);

          await xhr.post('/subject-enrollments', {
            data: {
              student: studentId,
              subject: subject.id,
              [programTypeKey]: currentProgram.id,
            },
          });
        }
      }

      const previousPaymentSubjectIds = getPaymentSubjectIds(payment);

      const nextPaymentSubjectIds = [
        ...new Set([...previousPaymentSubjectIds, ...selectedSubjectIds]),
      ];

      await xhr.put(`/payments/${payment.id}`, {
        data: {
          subjects: nextPaymentSubjectIds,
          revised: true,
        },
      });

      if (typeof setPayments === 'function') {
        setPayments((prev) => {
          const list = Array.isArray(prev) ? prev : payments;

          return list.map((item) => {
            if (item.id !== payment.id) return item;

            return {
              ...item,
              revised: true,
              subjects: nextPaymentSubjectIds,
            };
          });
        });
      }

      toast({
        title: 'Materias matriculadas',
        description: 'Las materias se han matriculado correctamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      handleClose();
    } catch (error) {
      console.error('Error matriculando materias:', error);

      toast({
        title: 'Error',
        description: 'No se pudieron matricular las materias.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />

      <ModalContent maxH="90vh">
        <ModalHeader>Matricular materias</ModalHeader>
        <ModalCloseButton />

        <ModalBody fontSize="sm" overflowY="auto">
          <Box mb={4}>
            <Text>
              <b>Estudiante:</b>{' '}
              {`${student?.user?.first_name || ''} ${student?.user?.last_name || ''}`.trim() ||
                student?.id ||
                'N/A'}
            </Text>

            <Text>
              <b>Pago:</b> #{payment?.id || 'N/A'}
            </Text>

            <Text>
              <b>Monto:</b> {payment?.amount || 'N/A'}
            </Text>

            <Text>
              <b>Fecha:</b>{' '}
              {payment?.payment_date ? dayjs(payment.payment_date).format('YYYY-MM-DD') : 'N/A'}
            </Text>
          </Box>

          <Divider mb={4} />

          <Box mb={4}>
            <Text fontWeight="bold" mb={2}>
              Programa
            </Text>

            {loadingPrograms ? (
              <Spinner />
            ) : (
              <Select
                placeholder="Seleccione el programa"
                value={programValue}
                onChange={(event) => handleProgramChange(event.target.value)}
              >
                {programs.map((program) => (
                  <option key={getProgramValue(program)} value={getProgramValue(program)}>
                    {program.name}
                  </option>
                ))}
              </Select>
            )}
          </Box>

          {isPeriodMenu && (
            <Box mb={4}>
              <Text fontWeight="bold" mb={2}>
                Periodo
              </Text>

              <Select
                value={currentPeriod}
                onChange={(event) => {
                  setCurrentPeriod(event.target.value);
                  setSelectedSubjects([]);
                }}
              >
                {Object.keys(subjectsByPeriod)
                  .sort()
                  .map((period) => (
                    <option key={period} value={period}>
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </option>
                  ))}
              </Select>
            </Box>
          )}

          {currentProgram && (
            <Box border="1px solid #d9d9e1" borderRadius="md" overflow="hidden">
              <Box backgroundColor="#cacbf9" color="#3d37a5" fontWeight="500" padding="8px">
                ¿Qué materias paga el estudiante?
              </Box>

              {loadingSubjects || loadingEnrollments ? (
                <Flex justifyContent="center" paddingY="20px">
                  <Spinner />
                </Flex>
              ) : (
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 1fr"
                  padding="10px"
                  gridGap="12px"
                  maxH="45vh"
                  overflowY="auto"
                  overscrollBehavior="contain"
                  sx={{
                    touchAction: 'pan-y',
                  }}
                >
                  {displayedSubjects.map((subject) => {
                    const isEnrolled = enrolledIds.includes(subject.id);
                    const isSelected = selectedSubjectIds.includes(subject.id);

                    return (
                      <Flex key={subject.id}>
                        <Checkbox
                          isChecked={isEnrolled || isSelected}
                          isDisabled={isEnrolled}
                          textDecoration={isEnrolled ? 'line-through' : undefined}
                          cursor={isEnrolled ? 'not-allowed' : 'pointer'}
                          onChange={(event) => handleCheck(subject, event.target.checked)}
                        >
                          {subject.name}
                        </Checkbox>
                      </Flex>
                    );
                  })}

                  {!displayedSubjects.length && (
                    <Box gridColumn="1 / -1" textAlign="center" color="gray.500">
                      No se encontraron materias.
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button mr={3} onClick={handleClose} isDisabled={saving}>
            Cancelar
          </Button>

          <Button
            colorScheme="purple"
            onClick={saveEnrollments}
            isLoading={saving}
            isDisabled={!selectedSubjects.length || !currentProgram}
          >
            Matricular materias
          </Button>
        </ModalFooter>
      </ModalContent >
    </Modal >
  );
}